import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import * as moduleAPI from './api';

import classes from './MobileFamilyFriendsDialog.module.scss';

const FAMILY_ACCOUNT_TOKEN_KEY = 'flcMobileFamilyAccountToken';
const MAX_MOBILE_FAMILY_FRIENDS = 15;

const getFriendsFromSession = (session) => (
  Array.isArray(session?.members)
    ? session.members.filter(member => member.originalMember !== true)
    : []
);

const getFriendId = (friend) => String(friend?.id || friend?._id || '');

const MobileFamilyFriendsDialog = ({ open, primaryName, onClose }) => {
  const [friends, setFriends] = useState([]);
  const [originalFriends, setOriginalFriends] = useState([]);
  const [firstname, setFirstname] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState('idle');

  useEffect(() => {
    if (!open) return;
    let active = true;
    const familyAccountToken = localStorage.getItem(FAMILY_ACCOUNT_TOKEN_KEY) || '';

    setFirstname('');
    setFriends([]);
    setOriginalFriends([]);
    setError('');
    setSaveState('idle');
    setLoading(true);

    if (!familyAccountToken) {
      setError('Family access needs to be verified again.');
      setFriends([]);
      setOriginalFriends([]);
      setLoading(false);
      return () => { active = false; };
    }

    moduleAPI.getFamilySession(familyAccountToken)
      .then((session) => {
        if (!active) return;
        const loadedFriends = getFriendsFromSession(session);
        setFriends(loadedFriends);
        setOriginalFriends(loadedFriends);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError?.response?.data?.error || requestError?.message || 'Unable to load Family & Friends.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [open]);

  const cleanPendingFirstname = String(firstname || '').trim();

  const hasChanges = useMemo(() => {
    const originalIds = originalFriends.map(getFriendId).filter(Boolean).sort();
    const currentIds = friends
      .filter(friend => !friend.__draft)
      .map(getFriendId)
      .filter(Boolean)
      .sort();
    const draftNames = friends
      .filter(friend => friend.__draft)
      .map(friend => String(friend.firstname || '').trim())
      .filter(Boolean)
      .sort();

    return JSON.stringify(originalIds) !== JSON.stringify(currentIds)
      || draftNames.length > 0
      || Boolean(cleanPendingFirstname);
  }, [cleanPendingFirstname, friends, originalFriends]);

  if (!open) return null;

  const addFriend = () => {
    if (loading || saving || friends.length >= MAX_MOBILE_FAMILY_FRIENDS) return;

    const cleanFirstname = String(firstname || '').trim();
    if (!cleanFirstname) return;

    setFriends((current) => [
      ...current,
      {
        id: `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        firstname: cleanFirstname,
        __draft: true,
      },
    ]);
    setFirstname('');
    setError('');
    setSaveState('idle');
  };

  const removeFriend = (friendId) => {
    if (loading || saving) return;
    setFriends((current) => current.filter(friend => getFriendId(friend) !== String(friendId)));
    setError('');
    setSaveState('idle');
  };

  const saveChanges = async () => {
    if (loading || saving) return;

    if (!hasChanges) {
      setSaveState('saved');
      return;
    }

    const familyAccountToken = localStorage.getItem(FAMILY_ACCOUNT_TOKEN_KEY) || '';
    if (!familyAccountToken) {
      setError('Family access needs to be verified again.');
      return;
    }

    const currentPersistedIds = new Set(
      friends
        .filter(friend => !friend.__draft)
        .map(getFriendId)
        .filter(Boolean)
    );
    const friendsToRemove = originalFriends.filter(
      friend => getFriendId(friend) && !currentPersistedIds.has(getFriendId(friend))
    );
    const friendsToAdd = friends.filter(friend => friend.__draft);
    if (cleanPendingFirstname) {
      friendsToAdd.push({ firstname: cleanPendingFirstname, __draft: true });
    }

    setSaving(true);
    setSaveState('saving');
    setError('');

    try {
      let session = null;

      // Remove first so replacing a friend also works when the family is already at the limit.
      for (const friend of friendsToRemove) {
        session = await moduleAPI.removeFamilyFriend(familyAccountToken, getFriendId(friend));
      }

      for (const friend of friendsToAdd) {
        session = await moduleAPI.addFamilyFriend(familyAccountToken, friend.firstname);
      }

      if (!session) {
        session = await moduleAPI.getFamilySession(familyAccountToken);
      }

      if (!session || session.success === false || !Array.isArray(session.members)) {
        throw new Error(session?.error || 'Unable to save Family & Friends.');
      }

      const savedFriends = getFriendsFromSession(session);
      setFriends(savedFriends);
      setOriginalFriends(savedFriends);
      setFirstname('');
      setSaveState('saved');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('mobileFamilyFriendsUpdated', {
          detail: { members: session?.members || [] },
        }));
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || 'Unable to save Family & Friends.');
      setSaveState('error');

      // The API is not transactional. If one operation succeeded before another failed,
      // refresh the dialog so it reflects the actual server state instead of showing a phantom draft.
      try {
        const session = await moduleAPI.getFamilySession(familyAccountToken);
        const savedFriends = getFriendsFromSession(session);
        setFriends(savedFriends);
        setOriginalFriends(savedFriends);
      } catch (_refreshError) {
        // Keep the visible draft if the refresh itself is unavailable.
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true" aria-label="Family & Friends">
      <section className={classes.dialog}>
        <h2>Family &amp; Friends</h2>

        <div className={classes.addRow}>
          <button
            type="button"
            className={classes.addButton}
            onClick={addFriend}
            disabled={loading || saving || !String(firstname || '').trim() || friends.length >= MAX_MOBILE_FAMILY_FRIENDS}
            aria-label="Add family member or friend"
          >
            +
          </button>
          <input
            value={firstname}
            onChange={(event) => {
              setFirstname(event.target.value);
              setError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addFriend();
              }
            }}
            maxLength={40}
            placeholder="First name"
            autoFocus
            disabled={loading || saving}
          />
          <span className={classes.counter}>
            {friends.length}/{MAX_MOBILE_FAMILY_FRIENDS}
          </span>
        </div>

        {error && <p className={classes.error}>{error}</p>}

        <ul className={classes.list}>
          <li className={classes.primary}>
            <span>{primaryName || 'User'}</span>
            <small>Primary user</small>
          </li>
          {friends.map((friend) => (
            <li key={getFriendId(friend)}>
              <span>{friend.firstname}</span>
              <button
                type="button"
                onClick={() => removeFriend(getFriendId(friend))}
                aria-label={`Remove ${friend.firstname}`}
                disabled={loading || saving}
              >
                ×
              </button>
            </li>
          ))}
        </ul>

        <p className={classes.savedNote} aria-live="polite">
          {saveState === 'saving' && 'Saving changes...'}
          {saveState === 'error' && 'Changes were not saved.'}
          {saveState === 'saved' && 'Changes saved.'}
          {saveState === 'idle' && hasChanges && 'Click Save to keep these changes.'}
        </p>

        <div className={classes.actions}>
          <button
            type="button"
            className={classes.closeButton}
            onClick={onClose}
            disabled={loading || saving}
          >
            Close
          </button>
          <button
            type="button"
            className={classes.saveButton}
            onClick={saveChanges}
            disabled={loading || saving}
          >
            Save
          </button>
        </div>
      </section>
    </div>
  );
};

MobileFamilyFriendsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  primaryName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default MobileFamilyFriendsDialog;
